import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'store/reducers';

export type ClaimId = string;

type ClaimsState = {
    items: ClaimId[];
    isLoaded: boolean;
    lastResolvedAt?: number;
};

const initialState: ClaimsState = {
    items: [],
    isLoaded: false,
    lastResolvedAt: undefined,
};

const normalizeClaims = (input: string | string[] | null | undefined): ClaimId[] => {
    if (!input) {
        return [];
    }
    const claimsArray = Array.isArray(input) ? input : [input];

    const allParts = claimsArray.flatMap(claim => claim.split(','));
    const cleanedParts = allParts.map(part => part.trim()).filter(Boolean);

    return [...new Set(cleanedParts)];
};

export const claimsSlice = createSlice({
    name: 'claims',
    initialState,
    reducers: {
        setClaims: {
            reducer: (state, action: PayloadAction<ClaimId[]>) => {
                state.items = action.payload;
                state.isLoaded = true;
                state.lastResolvedAt = Date.now();
            },
            prepare: (input: string | string[]) => ({ payload: normalizeClaims(input) }),
        },
        clearClaims: () => initialState,
    },
});

export const { setClaims, clearClaims } = claimsSlice.actions;
export default claimsSlice.reducer;

export const selectClaimsLoaded = (rootState: RootState) => rootState.claims.isLoaded;
export const selectClaimsItems = (rootState: RootState) => rootState.claims.items;

export type ClaimRequest = {
    type: string;
    action: string;
    resource?: string;
};

export const makeSelectHasClaim = (req: ClaimRequest) =>
    createSelector([selectClaimsItems], items => {
        const prefix = `${req.type}:${req.action}`;

        if (req.resource == null) {
            return items.some(item => item === prefix || item === `${prefix}:*` || item.startsWith(`${prefix}:`));
        }

        const exact = `${prefix}:${req.resource}`;
        return items.some(item => item === prefix || item === `${prefix}:*` || item === exact);
    });
